import { Link } from 'react-router-dom';
import ResourceLayout from '../../components/resources/ResourceLayout';
import useSEO from '../../hooks/useSEO';
import { canonicalUrl } from '../../utils/testSite';
import { guidePageSchema } from '../../utils/schema';

// Only verified Q&A — items flagged ⚠️ VERIFY in the brief
// (certificate retention period, course-repetition rule, fingerprint clearance)
// are intentionally omitted until confirmed against azre.gov.
const FAQS = [
  {
    q: 'Does Code of Ethics count toward my 24 hours?',
    a: 'No. Code of Ethics is a REALTOR® association membership requirement, not an ADRE one. It doesn\'t count toward CE and isn\'t submitted to ADRE.'
  },
  {
    q: 'Does CE from another state transfer to Arizona?',
    a: 'No. Only ADRE-approved courses count toward Arizona renewal.'
  },
  {
    q: 'What does "elective" mean?',
    a: 'Any approved course in a category of your choice. Surplus hours from a mandatory category also flow into your elective bucket automatically — but electives never flow backward into mandatory categories.'
  },
  {
    q: "Why won't ADRE let me renew when I have more than 24 hours?",
    a: 'A category deficiency. The portal requires every mandatory category filled, not just a total. Check your Licensee Login to see which one is short.'
  },
  {
    q: "I'm taking broker pre-licensing. Can it count as CE?",
    a: "Yes, via waiver — submit ADRE's Continuing Education Waiver Request Form for the 90-hour Broker Pre-Licensing course, with a school letter (in progress) or certificate (completed)."
  },
  {
    q: 'What happens if I renew late?',
    a: 'Up to one year late: $125 fee (vs. $60) and no practicing until renewed. Past one year: license is gone; you start over.'
  },
  {
    q: 'When can I start taking CE for my next renewal?',
    a: 'The day your new two-year period begins. Courses must be taken within the current two-year window to count.'
  },
  {
    q: 'Is there a grace period to keep working after expiration?',
    a: 'No. Expiration means stop. The one-year window lets you fix the license, not keep practicing while you do.'
  },
  {
    q: 'How fast can certificates be in my hands?',
    a: 'With Steady Education, instantly on passing each course exam. You can finish a course at 9 PM and submit it to ADRE at 9:05.'
  },
];

const FAQ_SCHEMA = {
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a }
  })),
};

const RELATED = [
  { url: '/arizona-ce-provider-comparison', title: 'Best Arizona CE Providers (Compared)', desc: 'Every major ADRE-approved provider for 2026, online vs classroom.' },
  { url: '/arizona-real-estate-ce-requirements', title: 'Arizona CE Requirements 2026', desc: 'The complete 24-hour breakdown by category.' },
  { url: '/arizona-license-expired-late-renewal', title: 'Renewing Late', desc: 'What happens after expiration and how to fix it.' },
  { url: '/how-to-submit-ce-to-adre', title: 'How to Submit CE to ADRE', desc: 'Your school doesn\'t report your courses — you do.' },
];

export default function FaqPage() {
  useSEO({
    title: 'Arizona Real Estate CE: 12 Questions Agents Actually Ask',
    description: 'Quick answers: Code of Ethics, out-of-state CE, electives, category deficiencies, broker pre-licensing waivers, and more.',
    url: canonicalUrl('/arizona-ce-faq'),
    jsonLd: guidePageSchema(FAQ_SCHEMA, {
      title: 'Arizona CE FAQ',
      url: canonicalUrl('/arizona-ce-faq'),
    }),
  });

  return (
    <ResourceLayout
      breadcrumb="FAQ"
      title="Arizona Real Estate CE: Quick Answers to Real Questions"
      subtitle="Plain answers to the questions Arizona agents actually ask about CE — verified against azre.gov."
      relatedGuides={RELATED}
    >
      {FAQS.map((f, i) => (
        <div key={i} style={faqItem}>
          <h2 style={{ marginTop: i === 0 ? 0 : 28 }}>{f.q}</h2>
          <p style={{ marginBottom: 0 }}>{f.a}</p>
        </div>
      ))}

      <div style={inlineCta}>
        <strong>Got a question we missed?</strong> Email <a href="mailto:help@steadyeducation.com" style={{ color: '#2C453E', fontWeight: 600 }}>help@steadyeducation.com</a> — we'll answer and likely add it here.
        <div style={{ marginTop: 14 }}>
          <Link to="/arizona/courses" style={inlineCtaBtn}>See the $49.99 package →</Link>
        </div>
      </div>
    </ResourceLayout>
  );
}

const faqItem = { borderBottom: '1px solid #f0e9da', paddingBottom: 16, marginBottom: 4 };
const inlineCta = { background: '#FAF7F2', border: '1px solid #d4c5ad', borderRadius: 12, padding: '20px 24px', marginTop: 32 };
const inlineCtaBtn = { display: 'inline-block', background: '#2C453E', color: 'white', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 };
